/**
 * Which side of its number a target (or a KPI) is met on (direction
 * 2026-10-05). A share is a floor by default — "at least 30 % press" — but a
 * group that should shrink is a CAP: 55 % existing customers against a 50 %
 * target is a miss, and every surface that graded "actual ≥ target" as green
 * said the opposite. One reading for the hero chips, the proposal rows and
 * the analysis panel.
 */
export type TargetDirection = "AT_MOST";

export const isAtMost = (direction?: string | null): direction is TargetDirection => direction === "AT_MOST";

/** Met: at or above a floor, at or below a cap. */
export function targetMet(actual: number, target: number, direction?: string | null): boolean {
  return isAtMost(direction) ? actual <= target : actual >= target;
}

/**
 * Points on the GOOD side of the target (negative = missing it): a floor's
 * `actual − target`, a cap's `target − actual` — so "−5" always means "5
 * points short", whichever way the target points.
 */
export function targetMargin(actual: number, target: number, direction?: string | null): number {
  return isAtMost(direction) ? target - actual : actual - target;
}

/**
 * What a cohort target says ON THE WIRE (ship 2026-10-06, audit r1 F-L2):
 * every row names its direction — "AT_MOST" for a cap, "AT_LEAST" for a
 * floor. main-backend reads an ABSENT direction as "keep the stored one", so
 * a tab still on an older bundle cannot turn a cap into a floor; the price is
 * that a floor nobody says out loud never lands. Read back, a floor is
 * absent — "AT_LEAST" exists only on the way in.
 */
export type WireTargetDirection = "AT_MOST" | "AT_LEAST";

export const wireTargetDirection = (direction?: string | null): WireTargetDirection =>
  isAtMost(direction) ? "AT_MOST" : "AT_LEAST";

/** "≤ " in front of a cap's number; nothing for a floor — language-neutral on purpose. */
export const capPrefix = (direction?: string | null): string => (isAtMost(direction) ? "≤ " : "");
